/**
 * Crop yield cap form. uc-core DTO (src/modules/gate/dto/reference.dto.ts CreateCropYieldCapDto):
 *   maxKgPerAcre               /^\d{1,7}(\.\d)?$/ and > 0
 *   flatCapKgPerFarmerSeason?  /^\d{1,9}(\.\d)?$/
 *   district?                  ≤ 60 chars (empty = every district)
 *   source                     required, ≤ 120 chars
 * Crop and district are fixed once created (the update DTO refuses them).
 */

export interface YieldCapDraft {
  cropId: string
  district: string
  maxKgPerAcre: string
  flatCap: string
  source: string
}

export interface YieldCapProblems {
  cropId?: string
  district?: string
  maxKgPerAcre?: string
  flatCap?: string
  source?: string
}

const PER_ACRE_RE = /^\d{1,7}(\.\d)?$/
const FLAT_RE = /^\d{1,9}(\.\d)?$/

export const emptyYieldCapDraft = (): YieldCapDraft => ({ cropId: '', district: '', maxKgPerAcre: '', flatCap: '', source: '' })

/** "1200.0" → "1200"; keeps one decimal when it is not zero. */
export function trimKg(v: string | null | undefined): string {
  if (!v) return ''
  return v.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1')
}

export function draftFromYieldCap(c: {
  cropId: string | null
  district: string | null
  maxKgPerAcre: string
  flatCapKgPerFarmerSeason: string | null
  source: string
}): YieldCapDraft {
  return {
    cropId: c.cropId ?? '',
    district: c.district ?? '',
    maxKgPerAcre: trimKg(c.maxKgPerAcre),
    flatCap: trimKg(c.flatCapKgPerFarmerSeason),
    source: c.source,
  }
}

const clean = (s: string) => s.trim().replace(/,/g, '')

export function validateYieldCapDraft(d: YieldCapDraft, mode: 'create' | 'edit'): YieldCapProblems {
  const p: YieldCapProblems = {}
  if (mode === 'create') {
    if (!d.cropId) p.cropId = 'Choose the crop'
    if (d.district.trim().length > 60) p.district = 'At most 60 characters'
  }
  const max = clean(d.maxKgPerAcre)
  if (!max) p.maxKgPerAcre = 'Enter the most kg an acre can give'
  else if (!PER_ACRE_RE.test(max)) p.maxKgPerAcre = 'Whole kg or one decimal, e.g. 1200 or 1200.5'
  else if (Number(max) <= 0) p.maxKgPerAcre = 'Must be more than 0'
  const flat = clean(d.flatCap)
  if (flat && !FLAT_RE.test(flat)) p.flatCap = 'Whole kg or one decimal, e.g. 2500'
  else if (flat && Number(flat) <= 0) p.flatCap = 'Leave empty for no flat cap'
  const source = d.source.trim()
  if (!source) p.source = 'Say where the figure comes from'
  else if (source.length > 120) p.source = 'At most 120 characters'
  return p
}

export const yieldCapOk = (p: YieldCapProblems) => Object.values(p).every((v) => !v)

/** POST body. Call only when valid. */
export function createYieldCapBody(d: YieldCapDraft) {
  const district = d.district.trim()
  const flat = clean(d.flatCap)
  return {
    cropId: d.cropId,
    ...(district ? { district } : {}),
    maxKgPerAcre: clean(d.maxKgPerAcre),
    ...(flat ? { flatCapKgPerFarmerSeason: flat } : {}),
    source: d.source.trim(),
  }
}
/** PATCH body: an emptied flat cap is cleared (null). Call only when valid. */
export function updateYieldCapBody(d: YieldCapDraft) {
  const flat = clean(d.flatCap)
  return { maxKgPerAcre: clean(d.maxKgPerAcre), flatCapKgPerFarmerSeason: flat || null, source: d.source.trim() }
}
